import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { reqSingleQuestionAndResponse } from "../requests/user";
import { MobileWrapper } from "./CMobileNotice";
import CustomerPageTitle from "./CustomerPageTitle";

const CQuestionPage = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState<any>();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    async function getSelectedQuestion() {
      const res = await reqSingleQuestionAndResponse(Number(questionId));
      setQuestion(res.data);
    }
    getSelectedQuestion();
  }, [questionId]);

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  };

  return (
    <PageContainer>
      {isMobile ? (
        <MobileWrapper>
          <CustomerPageTitle subtitle1="Support" title="Inquiry Response" />
          <BackButton onClick={() => navigate("/customer/support")}>
            <BackButtonText>Back</BackButtonText>
          </BackButton>

          {question && (
            <MobileCardContainer>
              <MobileHeader>
                <MobileTitle>{question.title}</MobileTitle>
                <MobileMetaRow>
                  <MetaItem>
                    <MetaLabel>Inquiry Date:</MetaLabel>
                    <MetaValue>{formatDate(question.createdAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Response Date:</MetaLabel>
                    <MetaValue>
                      {question.responseAt
                        ? formatDate(question.responseAt)
                        : "Pending"}
                    </MetaValue>
                  </MetaItem>
                </MobileMetaRow>
              </MobileHeader>

              <ContentSection>
                <SectionTitle>Inquiry Content</SectionTitle>
                <ContentText>{question.questionContent}</ContentText>

                {question.imageUrl && (
                  <ImageContainer>
                    <StyledImage
                      src={question.imageUrl}
                      alt="Inquiry image"
                      onClick={() => handleImageClick(question.imageUrl)}
                    />
                    <ImageCaption>Click image to enlarge</ImageCaption>
                  </ImageContainer>
                )}
              </ContentSection>

              <ContentSection>
                <SectionTitle>
                  <span>{question.manager}</span>'s Response
                </SectionTitle>
                <ContentText>{question.responseContent}</ContentText>

                {question.answerImageUrl && (
                  <ImageContainer>
                    <StyledImage
                      src={question.answerImageUrl}
                      alt="Response image"
                      onClick={() => handleImageClick(question.answerImageUrl)}
                    />
                    <ImageCaption>Click image to enlarge</ImageCaption>
                  </ImageContainer>
                )}
              </ContentSection>
            </MobileCardContainer>
          )}
        </MobileWrapper>
      ) : (
        <PCContainer>
          <CustomerPageTitle title="Inquiry Response" />
          <BackButton onClick={() => navigate("/customer/support")}>
            <BackButtonText>Back</BackButtonText>
          </BackButton>

          {question && (
            <CardContainer>
              <CardHeader>
                <HeaderTitle>{question.title}</HeaderTitle>
                <HeaderMeta>
                  <MetaGroup>
                    <MetaLabel>Inquiry Date:</MetaLabel>
                    <MetaValue>{formatDate(question.createdAt)}</MetaValue>
                  </MetaGroup>
                  <MetaGroup>
                    <MetaLabel>Response Date:</MetaLabel>
                    <MetaValue>
                      {question.responseAt
                        ? formatDate(question.responseAt)
                        : "Pending"}
                    </MetaValue>
                  </MetaGroup>
                </HeaderMeta>
              </CardHeader>

              <CardSection>
                <SectionTitle>Inquiry Content</SectionTitle>
                <ContentText>{question.questionContent}</ContentText>

                {question.imageUrl && (
                  <ImageContainer>
                    <StyledImage
                      src={question.imageUrl}
                      alt="Inquiry image"
                      onClick={() => handleImageClick(question.imageUrl)}
                    />
                    <ImageCaption>Click image to enlarge</ImageCaption>
                  </ImageContainer>
                )}
              </CardSection>

              <CardSection>
                <SectionTitle>
                  <ManagerName>{question.manager}</ManagerName>'s Response
                </SectionTitle>
                <ContentText>{question.responseContent}</ContentText>

                {question.answerImageUrl && (
                  <ImageContainer>
                    <StyledImage
                      src={question.answerImageUrl}
                      alt="Response image"
                      onClick={() => handleImageClick(question.answerImageUrl)}
                    />
                    <ImageCaption>Click image to enlarge</ImageCaption>
                  </ImageContainer>
                )}
              </CardSection>
            </CardContainer>
          )}
        </PCContainer>
      )}

      {/* Image Modal */}
      {imageModalOpen && selectedImage && (
        <ImageModalOverlay onClick={closeImageModal}>
          <ImageModal onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeImageModal}>&times;</CloseButton>
            <FullSizeImage src={selectedImage} alt="Enlarged image" />
          </ImageModal>
        </ImageModalOverlay>
      )}
    </PageContainer>
  );
};

export default CQuestionPage;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100%;
  background-color: #f5f7fa;
`;

const PCContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 900px) {
    padding: 1.5rem;
  }
`;

const CardContainer = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-top: 1.5rem;
`;

const MobileCardContainer = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-top: 1.5rem;
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  background-color: #f8f9ff;
  border-bottom: 1px solid #eaedf4;
`;

const HeaderTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

const MetaGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MobileHeader = styled.div`
  padding: 1.25rem;
  background-color: #f8f9ff;
  border-bottom: 1px solid #eaedf4;
`;

const MobileTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
`;

const MobileMetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetaLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
`;

const MetaValue = styled.span`
  font-size: 0.875rem;
  color: #4b5563;
`;

const CardSection = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eaedf4;

  &:last-child {
    border-bottom: none;
  }
`;

const ContentSection = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid #eaedf4;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ManagerName = styled.span`
  color: #3b82f6;
`;

const ContentText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #4b5563;
  white-space: pre-line;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.625rem 1rem;
  font-weight: 500;
  color: #fff;
  background-color: #3b82f6;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.25);

  &:hover {
    background-color: #2563eb;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(59, 130, 246, 0.2);
  }
`;

const BackButtonText = styled.span`
  font-size: 0.875rem;
`;

const ImageContainer = styled.div`
  margin: 1.25rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ImageCaption = styled.p`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
`;

const ImageModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.25rem;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ImageModal = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  background-color: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: zoomIn 0.2s ease-in-out;

  @keyframes zoomIn {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const FullSizeImage = styled.img`
  max-width: 100%;
  max-height: calc(90vh - 60px);
  object-fit: contain;
  border-radius: 8px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.625rem;
  right: 0.625rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  cursor: pointer;
  z-index: 1001;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    color: #ef4444;
    background: white;
    transform: scale(1.1);
  }
`;
